import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Info } from "lucide-react";
import { http } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

// Lote 64 — avisos sobre a saúde da busca de publicações e sobre o atraso da fonte (DJEN).
//
// Por quê: em 07/10/2026 intimações do TRT15 já estavam na AASP e no serviço da OAB, mas a
// API pública do DJEN (de onde o app lê) só as expôs dias depois. O app não tinha como
// avisar — e a ausência de publicação parece "não há nada". Agora:
//  - `variant="painel"`: só aparece quando há problema (falha, busca parada ou dias sem
//    nada novo), com link para a caixa de Publicações. Sem problema, não ocupa espaço.
//  - `variant="caixa"`: mesmos alertas + um aviso fixo e discreto de que publicações podem
//    chegar atrasadas, com a medição real do atraso dos últimos 60 dias.

const STATUS_STYLE = {
  error: {
    box: "border-red-200 bg-red-50 text-red-900",
    icon: AlertTriangle,
    iconCls: "text-red-600",
    title: "A última busca de publicações falhou",
  },
  stale: {
    box: "border-red-200 bg-red-50 text-red-900",
    icon: AlertTriangle,
    iconCls: "text-red-600",
    title: "A busca automática de publicações está parada",
  },
  quiet: {
    box: "border-amber-200 bg-amber-50 text-amber-900",
    icon: Clock,
    iconCls: "text-amber-600",
    title: "Muitos dias sem publicação nova",
  },
};

const QUIET_HINT =
  "Pode ser que realmente não haja nada, ou que o DJEN ainda não tenha liberado as intimações. Para prazos, confira também o recorte da AASP ou o diário.";
const BROKEN_HINT =
  "Enquanto isso, intimações novas podem não aparecer aqui. Para prazos urgentes, confira também o recorte da AASP ou o diário. O app tenta de novo às 6h e às 16h — ou clique em Sincronizar na caixa de Publicações.";

function plural(n, one, many) {
  return n === 1 ? one : many;
}

function formatMoment(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit" });
  const time = d.toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" });
  return `${date} às ${time}`;
}

export default function SyncHealthBanner({ variant = "painel", refreshKey = 0 }) {
  const [health, setHealth] = useState(null);

  const load = useCallback(
    () => http.get("/publicacoes/saude").then((r) => setHealth(r.data)).catch(() => {}),
    []
  );
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  useAutoRefresh(load);

  if (!health) return null;
  // Lote 72: a caixa de Publicações esconde o aviso fixo de atraso dentro do botão "Como
  // funciona?" — variant "aviso" desenha só esse texto; "caixa" e "painel" só os alertas.
  const style = variant === "aviso" ? null : STATUS_STYLE[health.status];
  const delay = health.delay || {};
  const showNotice = variant === "aviso";

  if (!style && !showNotice) return null;

  const Icon = style?.icon;
  const hint = health.status === "quiet" ? QUIET_HINT : BROKEN_HINT;

  return (
    <div className={variant === "aviso" ? "" : "space-y-2 mb-5"} data-testid={`sync-health-${variant}`}>
      {style && (
        <div
          role="alert"
          data-testid="sync-health-alert"
          data-status={health.status}
          className={`flex gap-3 rounded-md border px-4 py-3 text-sm ${style.box}`}
        >
          <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.iconCls}`} strokeWidth={2} />
          <div className="min-w-0">
            <p className="font-semibold">{style.title}</p>
            {(health.messages || []).map((m, i) => (
              <p key={i} className="mt-0.5 break-words">{m}</p>
            ))}
            <p className="mt-1.5 opacity-90">{hint}</p>
            {health.last_attempt_at && (
              <p className="mt-1 text-xs opacity-75">
                Última tentativa: {formatMoment(health.last_attempt_at)}
                {health.last_ok_at ? ` · última sem erro: ${formatMoment(health.last_ok_at)}` : ""}
              </p>
            )}
            {variant === "painel" && (
              <Link to="/publicacoes" className="inline-block mt-2 text-xs font-semibold underline underline-offset-2">
                Abrir caixa de Publicações
              </Link>
            )}
          </div>
        </div>
      )}

      {showNotice && (
        <div
          data-testid="sync-delay-notice"
          className="flex gap-3 rounded-md border border-stone-200 bg-stone-50 px-4 py-3 text-xs text-stone-600"
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-stone-400" strokeWidth={2} />
          <div className="min-w-0">
            <p>
              <span className="font-semibold text-stone-700">Publicações podem chegar atrasadas.</span>{" "}
              O app lê o DJEN (fonte pública do CNJ) pela busca por OAB, que às vezes demora dias para
              liberar uma intimação que a AASP e o serviço da OAB já mostram, e também pelo caderno
              diário dos tribunais, que sai no próprio dia. Para prazos, não dependa só do app:
              confira também o recorte e o diário.
            </p>
            {delay.sample > 0 ? (
              <p className="mt-1.5" data-testid="sync-delay-stats">
                Medido nos últimos {delay.window_days} dias ({delay.sample} {plural(delay.sample, "publicação", "publicações")}):
                mediana de {delay.median_bd} {plural(delay.median_bd, "dia útil", "dias úteis")} entre a
                disponibilização e a chegada ao app; maior atraso: {delay.max_bd}{" "}
                {plural(delay.max_bd, "dia útil", "dias úteis")}
                {delay.late_count > 0
                  ? `; ${delay.late_count} ${plural(delay.late_count, "chegou", "chegaram")} com ${delay.late_threshold_bd} ou mais dias úteis de atraso`
                  : "; nenhuma com atraso relevante"}
                .
              </p>
            ) : (
              <p className="mt-1.5">Ainda não há publicações suficientes para medir o atraso.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
